<script lang="ts">
import { inject } from 'vue'
import Overlay from 'ol/Overlay'
import Draw from 'ol/interaction/Draw'
import { getArea, getLength } from 'ol/sphere'
import VectorSource from 'ol/source/Vector.js'
import VectorLayer from 'ol/layer/Vector.js'
import LineString from 'ol/geom/LineString'
import Polygon from 'ol/geom/Polygon'
import ControlBar from 'ol-ext/control/Bar'
import ControlToggleButton from 'ol-ext/control/Toggle'
import ControlButton from 'ol-ext/control/Button'
import Map from 'ol/Map'
import { ProjectionLike } from 'ol/proj'

export default {
    // https://viglino.github.io/ol-ext/examples/popup/map.tooltip.measure.html
    // https://openlayers.org/en/latest/examples/measure.html
    name: 'DkOlMeasure',
    components: {},
    emits: ['isMeasuring'],
    props: {
        showHectare: { type: Boolean, required: false },
    },
    data() {
        return {
            map: null,
            projection: null,
            draw: null,
            drawType: '',
            source: null,
            vectors: [],
            measureTooltipElement: null,
            measureTooltip: null,
        }
    },

    mounted() {
        const map: Map = inject('map')
        this.map = map
        this.projection = (map.getView()?.getProjection() ?? 'EPSG:3857') as ProjectionLike

        this.source = new VectorSource()
        this.initBarControl()
        // Draw init when toggle polygon or linestring is clicked
    },
    methods: {
        initBarControl() {
            // Main control bar
            const mainbar = new ControlBar({ className: 'measure-control' })
            this.map.addControl(mainbar)

            // https://viglino.github.io/ol-ext/examples/bar/map.control.bar.html
            mainbar.setPosition('right')

            const nested = new ControlBar({ toggleOne: true, group: true, className: 'mr-2' })
            mainbar.addControl(nested)

            /* AREA BUTTON */
            const AreaMeasureButton = new ControlToggleButton({
                html: '<span class=\'mdi mdi-vector-polygon\'></span>',
                className: 'measure-area',
                title: 'Mesurer des superficies',
                onToggle: (active) => {
                    this.switchMeasureMode(active ? 'area' : '')
                },
            })
            nested.addControl(AreaMeasureButton)

            /* LENGTH BUTTON */
            const LengthMeasureButton = new ControlToggleButton({
                html: '<span class=\'mdi mdi-vector-line\'></span>',
                className: 'measure-length',
                title: 'Mesurer des distances',
                onToggle: (active) => {
                    this.switchMeasureMode(active ? 'length' : '')
                },
            })
            nested.addControl(LengthMeasureButton)

            /* DELETE BUTTON */
            const deleteMeasureButton = new ControlButton({
                html: '<span class=\'mdi mdi-delete\'></span>',
                className: 'ol-toggle measure-delete',
                title: 'Supprimer les mesures traçées',
                handleClick: () => {
                    this.vectors.forEach((vector) => {
                        this.map.removeLayer(vector)
                    })

                    // RESET DRAW
                    this.source = new VectorSource()
                    this.map.removeInteraction(this.draw)
                    LengthMeasureButton.setActive(false)
                    AreaMeasureButton.setActive(false)
                    this.$emit('isMeasuring', false)

                    // this.map.removeOverlay(this.measureTooltip) DOESNT WORK
                    document.querySelectorAll('.ol-tooltip-static').forEach((tooltip) => {
                        tooltip.remove()
                    })
                    document.querySelectorAll('.ol-tooltip-measure').forEach((tooltip) => {
                        tooltip.remove()
                    })
                },
            })
            mainbar.addControl(deleteMeasureButton)
        },
        redreshDrawType() {
            // TO DO : fill-color double when drawtype change. probably map is doubling layers
            const vector = new VectorLayer({
                source: this.source,
                style: {
                    // 'fill-color': 'rgba(255, 255, 255, 0.2)',
                    'stroke-color': '#ffcc33',
                    'stroke-width': 2,
                    'circle-radius': 7,
                    'circle-fill-color': '#ffcc33',
                },
            })
            this.map.addLayer(vector)
            this.vectors.push(vector)

            this.createMeasureTooltip()
            this.initDraw()

            this.draw.on('drawstart', (evt) => {
                // set sketch
                const sketch = evt.feature

                let tooltipCoord

                sketch.getGeometry().on('change', (evt) => {
                    const geom = evt.target
                    let output
                    if (geom instanceof Polygon) {
                        output = this.formatArea(geom)
                        tooltipCoord = geom.getInteriorPoint().getCoordinates()
                    }
                    else if (geom instanceof LineString) {
                        output = this.formatLength(geom)
                        tooltipCoord = geom.getLastCoordinate()
                    }
                    this.measureTooltipElement.innerHTML = output
                    this.measureTooltip.setPosition(tooltipCoord)
                    // map.removeInteraction(this.draw)
                })
            })
            this.draw.on('drawend', () => {
                this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-static'
                this.measureTooltip.setOffset([0, -7])

                // unset tooltip so that a new one can be created
                this.measureTooltipElement = null
                this.createMeasureTooltip()
                // unByKey(this.listener)
            })
        },
        initDraw() {
            const type = this.drawType === 'area' ? 'Polygon' : 'LineString'

            this.draw = new Draw({
                source: this.source,
                type,
                // style(feature) {
                //     const geometryType = feature.getGeometry().getType()
                //     if (geometryType === type || geometryType === 'Point') {
                //         return style
                //     }
                // },
            })
            this.map.addInteraction(this.draw)
        },
        createMeasureTooltip() {
            if (this.measureTooltipElement) {
                this.measureTooltipElement.remove()
            }
            this.measureTooltipElement = document.createElement('div')
            this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure'
            this.measureTooltip = new Overlay({
                element: this.measureTooltipElement,
                offset: [0, -15],
                positioning: 'bottom-center',
                stopEvent: false,
                insertFirst: false,
            })
            this.map.addOverlay(this.measureTooltip)
        },
        formatLength(line) {
            // console.log('formatLength-----------', line)
            const length = getLength(line, { projection: this.projection })
            let output
            if (length > 100) {
                output = `${Math.round((length / 1000) * 100) / 100} ` + `km`
            }
            else {
                output = `${Math.round(length * 100) / 100} ` + `m`
            }
            return output
        },
        formatArea(polygon) {
            const area = getArea(polygon, { projection: this.projection })
            let output
            if (this.showHectare) {
                output = `${Math.round((area / 1000000) * 10000) / 100} ` + `ha`
            }
            else {
                if (area > 10000) {
                    output = `${Math.round((area / 1000000) * 100) / 100} ` + `km<sup>2</sup>`
                }
                else {
                    output = `${Math.round(area * 100) / 100} ` + `m<sup>2</sup>`
                }
            }
            // console.log(area, output)
            return `${output} | ${this.formatLength(polygon)}`
        },
        switchMeasureMode(type: string) {
            console.log(type)
            this.drawType = type
            this.map.removeInteraction(this.draw)

            this.$emit('isMeasuring', !!this.drawType)

            if (this.drawType) {
                this.redreshDrawType()
            }
        },
    },
    render() {
        return null
    },
}
</script>

<template>
    <!--    <ol-control-bar class-name="measure-bar" position="left"> -->
    <!--        <ol-toggle-control -->
    <!--            html="<span class='mdi mdi-vector-polygon'></span>" -->
    <!--            class-name="measure-length" -->
    <!--            title="Mesurer des superficies" -->
    <!--            :on-toggle="(active) => switchMeasureMode(active ? 'area' : '')" -->
    <!--        /> -->
    <!--        <ol-toggle-control -->
    <!--            html="<span class='mdi mdi-vector-line'></span>" -->
    <!--            class-name="measure-area" -->
    <!--            title="Mesurer des distances" -->
    <!--            :on-toggle="(active) => switchMeasureMode(active ? 'length' : '')" -->
    <!--        /> -->
    <!--        <ol-control-button -->
    <!--            html="<span class='mdi mdi-delete'></span>" -->
    <!--            class-name="measure-remove" -->
    <!--            title="Supprimer les mesures traçées" -->
    <!--            :on-toggle="(active) => switchMeasureMode(active ? 'length' : '')" -->
    <!--        /> -->
    <!--    </ol-control-bar> -->
</template>

<style>
.measure-control {
    z-index: -1; /*layer toggle is below measure buttons*/
}

.ol-control {
    background-color: transparent !important;
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.7;
    white-space: nowrap;
    font-size: 12px;
    cursor: default;
    user-select: none;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: #ffcc33;
    color: black;
    border: 1px solid white;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: '';
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-static:before {
    border-top-color: #ffcc33;
}

.ol-toggle.ol-button.ol-control button {
    text-align: center;
    width: 30px;
}

.measure-delete button {
    color: #c7001a !important;
}
</style>
